import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const AdminSidebarNavigation = ({ isCollapsed = false, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const storedRole = localStorage.getItem('userRole');
    if (storedRole) {
      setUserRole(storedRole);
    }
  }, []);

  const allMenuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'LayoutDashboard',
      path: '/admin-dashboard',
      roles: ['super_admin', 'reviewer', 'data_user'],
    },
    {
      id: 'registrations',
      label: 'Registrations',
      icon: 'ClipboardList',
      path: '/admin-registrations',
      roles: ['super_admin', 'reviewer'],
    },
    {
      id: 'communications',
      label: 'Communications',
      icon: 'MessageSquare',
      path: '/admin-communication-center',
      roles: ['super_admin', 'comms_user'],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: 'FileText',
      path: '/data-export-dashboard',
      roles: ['super_admin', 'data_user'],
    },
    {
      id: 'player-editor',
      label: 'Player Editor',
      icon: 'UserEdit',
      path: '/super-admin-player-editor',
      roles: ['super_admin'],
    },
  ];

  // Filter menu items based on user role
  const menuItems = useMemo(() => {
    if (!userRole) return [];
    return allMenuItems.filter(item => 
      item.roles.includes(userRole) 
      && item.id !== 'communications' // Hide Communications tab
      && item.id !== 'player-editor'   // Temporarily hide Player Editor tab (feature available via direct URL)
    );
  }, [userRole]);

  const handleNavigation = (path) => {
    navigate(path);
    // Close mobile menu on navigation
    setIsMobileOpen(false);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      localStorage.removeItem('userRole');
      localStorage.removeItem('adminEmail');
      navigate('/admin-login');
    } catch (error) {
      console.error('Error signing out:', error);
      // Still clear local storage and navigate even if signOut fails
      localStorage.removeItem('userRole');
      localStorage.removeItem('adminEmail');
      navigate('/admin-login');
    }
  };

  return (
    <>
      <button
        onClick={toggleMobileMenu}
        className="admin-sidebar-toggle"
        aria-label="Toggle navigation menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>
      {isMobileOpen && (
        <div
          className="overlay lg:hidden"
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}
      <aside
        className={`admin-sidebar ${
          isCollapsed ? 'collapsed' : ''
        } ${isMobileOpen ? 'lg:translate-x-0 translate-x-0' : 'lg:translate-x-0 -translate-x-full'}`}
      >
        <div className="admin-sidebar-header">
          <div className="flex items-center">
            <div className="admin-sidebar-logo">
              <Icon name="Trophy" size={24} color="var(--color-primary)" />
            </div>
            <span className="admin-sidebar-brand">MI Cricket</span>
          </div>
          <button
            onClick={onToggle}
            className="hidden lg:block p-2 hover:bg-muted rounded-md transition-colors"
            aria-label="Toggle sidebar"
          >
            <Icon
              name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'}
              size={20}
            />
          </button>
        </div>

        <nav className="admin-sidebar-nav">
          {menuItems?.map((item) => (
            <button
              key={item?.id}
              onClick={() => handleNavigation(item?.path)}
              className={`admin-sidebar-item ${
                location?.pathname === item?.path ? 'active' : ''
              }`}
              aria-label={item?.label}
            >
              <Icon name={item?.icon} size={20} />
              <span className="admin-sidebar-item-text">{item?.label}</span>
            </button>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="admin-sidebar-footer">
          <button
            onClick={handleLogout}
            className="admin-sidebar-item text-error hover:bg-error/10"
            aria-label="Logout"
          >
            <Icon name="LogOut" size={20} />
            <span className="admin-sidebar-item-text">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebarNavigation;